import { atlasApi } from './atlasApi';
import { hyperliquidClient } from './hyperliquid';
import type { Hip3Market } from '../types';

export interface MarketStats {
  lastPrice: number;
  oraclePrice?: number;
  change24h: number;
  funding8h: number;
  volume24h: number;
  openInterest: number;
}

interface AssetContext {
  dayNtlVlm: string;
  funding: string;
  impactPxs: [string, string] | null;
  markPx: string;
  midPx: string | null;
  openInterest: string;
  oraclePx: string;
  premium: string | null;
  prevDayPx: string;
}

class MarketDataService {
  private statsCache: Map<string, MarketStats> = new Map();
  private lastUpdate: number = 0;
  private updateInterval: number = 30000; // 30 seconds (backend handles polling)
  private useBackend: boolean = true;

  async fetchMarketStats(markets: Hip3Market[]): Promise<Map<string, MarketStats>> {
    const now = Date.now();

    // Return cache if still fresh
    if (this.statsCache.size > 0 && now - this.lastUpdate < this.updateInterval) {
      return this.statsCache;
    }

    // Try to use backend API first
    if (this.useBackend) {
      try {
        const response = await atlasApi.getMarkets();
        const newStats = new Map<string, MarketStats>();

        // Convert backend response to MarketStats map
        for (const marketData of response.markets) {
          // Backend returns aggregated stats per baseAsset
          // We need to map each venue to the same stats
          for (const venue of marketData.venues) {
            newStats.set(venue.symbol, {
              lastPrice: marketData.aggregatedStats.markPrice,
              oraclePrice: marketData.aggregatedStats.oraclePrice,
              change24h: marketData.aggregatedStats.change24h,
              funding8h: marketData.aggregatedStats.funding8h,
              volume24h: marketData.aggregatedStats.volume24h,
              openInterest: marketData.aggregatedStats.openInterest,
            });
          }
        }

        this.statsCache = newStats;
        this.lastUpdate = now;

        console.log(`✓ Updated market stats from backend for ${newStats.size} markets`);
        return newStats;
      } catch (error) {
        console.warn('Backend unavailable, falling back to direct Hyperliquid calls:', error);
        this.useBackend = false;
        // Fall through to legacy code
      }
    }

    // Fallback: Direct Hyperliquid calls (legacy behavior)
    try {
      // Group markets by DEX
      const marketsByDex = new Map<string | undefined, Hip3Market[]>();
      for (const market of markets) {
        const dexKey = market.dexIndex || 'native';
        if (!marketsByDex.has(dexKey)) {
          marketsByDex.set(dexKey, []);
        }
        marketsByDex.get(dexKey)!.push(market);
      }

      const newStats = new Map<string, MarketStats>();

      // Fetch data for each DEX
      for (const [dexKey, dexMarkets] of marketsByDex) {
        try {
          const dexParam = dexKey === 'native' ? undefined : dexKey;

          // Fetch meta and asset contexts for this DEX
          const [meta, assetCtxs] = await hyperliquidClient.getMetaAndAssetCtxs(dexParam);

          // Map asset contexts by symbol
          // The API returns asset.name WITH prefix for HIP-3 (e.g., "vntl:ANTHROPIC")
          // and WITHOUT prefix for native (e.g., "BTC")
          const ctxMap = new Map<string, AssetContext>();
          meta.universe.forEach((asset: any, idx: number) => {
            ctxMap.set(asset.name, assetCtxs[idx]);
          });

          // Process each market
          for (const market of dexMarkets) {
            // Use market.symbol directly - it matches what the API returns
            // HIP-3: market.symbol = "vntl:ANTHROPIC" matches asset.name = "vntl:ANTHROPIC"
            // Native: market.symbol = "BTC" matches asset.name = "BTC"
            const ctx = ctxMap.get(market.symbol);
            if (!ctx) {
              // Skip markets that aren't in this dex's universe
              continue;
            }

            const markPx = parseFloat(ctx.markPx);
            const prevDayPx = parseFloat(ctx.prevDayPx);
            const change24h = ((markPx - prevDayPx) / prevDayPx) * 100;

            // Funding is 8h rate, already in percentage-like format
            // The API returns it as a decimal (e.g., 0.0001 = 0.01%)
            const funding8h = parseFloat(ctx.funding) * 100;

            newStats.set(market.id, {
              lastPrice: markPx,
              oraclePrice: parseFloat(ctx.oraclePx),
              change24h,
              funding8h,
              volume24h: parseFloat(ctx.dayNtlVlm),
              openInterest: parseFloat(ctx.openInterest),
            });
          }
        } catch (error) {
          console.error(`Error fetching stats for DEX ${dexKey}:`, error);
        }
      }

      this.statsCache = newStats;
      this.lastUpdate = now;

      console.log(`Updated market stats (fallback) for ${newStats.size} markets`);
      return newStats;
    } catch (error) {
      console.error('Error fetching market stats:', error);
      return this.statsCache;
    }
  }

  getStats(marketId: string): MarketStats | undefined {
    return this.statsCache.get(marketId);
  }

  clearCache(): void {
    this.statsCache.clear();
    this.lastUpdate = 0;
  }
}

export const marketDataService = new MarketDataService();
